import React, { useEffect, useRef, useState } from 'react';
import { useMediaQuery } from 'usehooks-ts';
import { useTranslation } from 'react-i18next';
import { useImageSizes } from '../useImageNaturalSize';
import * as S from './GalleryModal.styles';
import { CloseIcon } from '@/components/UI/Icons/Icons';
import {
  GalleryImage,
  GalleryModalProps,
  GalleryItemType,
} from '@/api/types/shop.types';
import useScreenSize from '@/hooks/useScreenSize';
import { handleMobileVideoStart } from '@/components/Shop/ShopView.helpers';
import { KeyboardKey } from '@/components/Shop/Shop.constants';

export const GalleryModal: React.FC<GalleryModalProps> = ({
  items,
  activeIndex,
  setActiveIndex,
  onClose,
  shopName,
  shopWorkTime,
  workingDays,
  phoneNumbers,
  getPhoneNumbers,
  toggleContacts,
}) => {
  const overlayRef = useRef<HTMLDivElement>(null);
  const thumbsRef = useRef<(HTMLDivElement | null)[]>([]);
  const [activeVideoIndices, setActiveVideoIndices] = useState<Set<number>>(
    new Set()
  );
  const [offsetX, setOffsetX] = useState(0);
  const [isSwiping, setIsSwiping] = useState(false);
  const [showPhones, setShowPhones] = useState(false);
  const touchStartX = useRef<number | null>(null);
  const prevIndexRef = useRef<number>(activeIndex);
  const { t } = useTranslation();
  const isMobile = useMediaQuery('(max-width: 768px)');
  const { isLarge } = useScreenSize();

  const allImageSrcs = items
    .filter((i): i is GalleryImage => i.type === 'image')
    .map(i => i.src);
  const imageSizes = useImageSizes(allImageSrcs);

  useEffect(() => {
    if (!window.YT) {
      const tag = document.createElement('script');
      tag.src = 'https://www.youtube.com/iframe_api';
      document.body.appendChild(tag);
    }
  }, []);

  useEffect(() => {
    const prev = prevIndexRef.current;
    const prevItem = items[prev];
    if (prevItem?.type === 'youtube' && isLarge) {
      const iframe = document.querySelector<HTMLIFrameElement>(
        `#yt-frame-${prev}`
      );
      iframe?.contentWindow?.postMessage(
        JSON.stringify({ event: 'command', func: 'stopVideo', args: [] }),
        '*'
      );
    }
    prevIndexRef.current = activeIndex;
    setActiveVideoIndices(new Set());
  }, [activeIndex]);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    const handleKeyDown = (e: KeyboardEvent) => {
      switch (e.key) {
        case KeyboardKey.Escape:
          onClose();
          break;
        case KeyboardKey.ArrowLeft:
          setActiveIndex((activeIndex - 1 + items.length) % items.length);
          break;
        case KeyboardKey.ArrowRight:
          setActiveIndex((activeIndex + 1) % items.length);
          break;
        default:
          break;
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [activeIndex]);

  useEffect(() => {
    const el = thumbsRef.current[activeIndex];
    el?.scrollIntoView({ behavior: 'smooth', inline: 'center' });
  }, [activeIndex]);

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
    setIsSwiping(true);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (touchStartX.current !== null) {
      setOffsetX(e.touches[0].clientX - touchStartX.current);
    }
  };

  const handleTouchEnd = () => {
    if (offsetX > 50) {
      setActiveIndex((activeIndex - 1 + items.length) % items.length);
    } else if (offsetX < -50) {
      setActiveIndex((activeIndex + 1) % items.length);
    }
    setOffsetX(0);
    setIsSwiping(false);
  };

  if (!items.length || activeIndex < 0 || activeIndex >= items.length)
    return null;

  const showCallNumbers = () => {
    if (!phoneNumbers?.length) getPhoneNumbers();
    if (!isLarge && toggleContacts) toggleContacts();
    else setShowPhones(prev => !prev);
  };

  return (
    <S.Overlay
      ref={overlayRef}
      onClick={e => e.target === overlayRef.current && onClose()}
    >
      <S.Backdrop>
        <S.TopBar>
          <S.ShopInfo>
            <S.ShopName>{shopName}</S.ShopName>
            {shopWorkTime && (
              <S.ShopWorkTime>
                {workingDays && `${workingDays} `}
                {shopWorkTime}
              </S.ShopWorkTime>
            )}
          </S.ShopInfo>
          <S.HeaderButtons>
            <S.CallButton onClick={showCallNumbers}>
              {t('shop.show_number')}
            </S.CallButton>
            {showPhones && isLarge && (
              <S.PhonePopup>
                {phoneNumbers?.map((phone, i) => (
                  <S.PhoneItem key={i}>{phone}</S.PhoneItem>
                ))}
              </S.PhonePopup>
            )}
          </S.HeaderButtons>
          <S.CloseButton onClick={onClose}>
            <CloseIcon fill="white" />
          </S.CloseButton>
        </S.TopBar>

        <S.SlideArea
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          <S.SlideWrapper
            $activeIndex={activeIndex}
            $offsetX={offsetX}
            $isSwiping={isSwiping}
          >
            {items.map((item, idx) => (
              <S.Slide key={idx}>
                {item.type === GalleryItemType.YouTube ? (
                  isLarge ? (
                    <S.VideoWrapper>
                      <iframe
                        id={`yt-frame-${idx}`}
                        width="100%"
                        height="100%"
                        src={`https://www.youtube.com/embed/${item.videoId}?rel=0&modestbranding=1&controls=1&enablejsapi=1`}
                        title={item.alt}
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      />
                    </S.VideoWrapper>
                  ) : activeVideoIndices.has(idx) ? (
                    <S.VideoWrapper>
                      <div
                        id={`yt-player-${idx}`}
                        style={{ width: '100%', height: '100%' }}
                      />
                    </S.VideoWrapper>
                  ) : (
                    <S.ImageWrapper
                      onClick={() =>
                        handleMobileVideoStart(
                          idx,
                          item.videoId,
                          setActiveVideoIndices
                        )
                      }
                    >
                      <img
                        src={`https://img.youtube.com/vi/${item.videoId}/hqdefault.jpg`}
                        alt={item.alt}
                        width="100%"
                        height="100%"
                        style={{ objectFit: 'contain' }}
                        loading="lazy"
                      />
                      <S.PlayButton />
                    </S.ImageWrapper>
                  )
                ) : item.type === GalleryItemType.Image &&
                  imageSizes.has(item.src) ? (
                  <S.ImageWrapper>
                    <S.ImageGallery
                      src={item.src}
                      alt={item.alt}
                      fetchPriority="high"
                      width={isLarge ? imageSizes.get(item.src)?.width : '100%'}
                      height={
                        isLarge ? imageSizes.get(item.src)?.height : '100%'
                      }
                      $isLarge={isLarge}
                    />
                  </S.ImageWrapper>
                ) : null}
              </S.Slide>
            ))}
          </S.SlideWrapper>

          {(!isMobile ||
            items[activeIndex].type === GalleryItemType.YouTube) && (
            <>
              <S.Arrow
                $side="left"
                onClick={() =>
                  setActiveIndex(
                    (activeIndex - 1 + items.length) % items.length
                  )
                }
              />
              <S.Arrow
                $side="right"
                onClick={() => setActiveIndex((activeIndex + 1) % items.length)}
              />
            </>
          )}

          <S.SlideCounter>
            {activeIndex + 1} / {items.length}
          </S.SlideCounter>
        </S.SlideArea>

        <S.Thumbnails>
          {items.map((item, idx) => (
            <S.Thumbnail
              key={idx}
              ref={el => {
                thumbsRef.current[idx] = el;
              }}
              $active={idx === activeIndex}
              onMouseEnter={() => setActiveIndex(idx)}
            >
              <img
                src={
                  item.type === 'youtube'
                    ? `https://img.youtube.com/vi/${item.videoId}/hqdefault.jpg`
                    : item.src
                }
                alt={item.alt}
                width={120}
                height={80}
                style={{ objectFit: 'cover' }}
              />
            </S.Thumbnail>
          ))}
        </S.Thumbnails>
      </S.Backdrop>
    </S.Overlay>
  );
};
